/**
 * Document Merger Utility
 * 
 * Merges uploaded documents (PDFs, images) into the main export PDF.
 * Uses pdf-lib for PDF manipulation.
 * 
 * Limits:
 * - Max 10 documents
 * - Max 25MB total size
 * - Supported: PDF, JPEG, PNG
 */

import { PDFDocument, rgb } from 'pdf-lib';
import supabase from '../../supabase/supabase';

// Configuration
const CONFIG = {
  MAX_DOCUMENTS: 10,
  MAX_TOTAL_SIZE: 25 * 1024 * 1024, // 25MB
  IMAGE_MAX_WIDTH: 500,  // Max width in PDF points
  IMAGE_MAX_HEIGHT: 700, // Max height in PDF points
  PAGE_MARGIN: 50,
};

/**
 * Check if document type is supported (handles various formats)
 * Note: The upload form stores types like 'pdf', 'image', 'doc' - not MIME types
 */
const isSupportedType = (docType) => {
  if (!docType) return false;
  const type = docType.toLowerCase();
  return (
    type === 'application/pdf' ||
    type === 'pdf' ||
    type === 'image/jpeg' ||
    type === 'image/jpg' ||
    type === 'jpeg' ||
    type === 'jpg' ||
    type === 'image/png' ||
    type === 'png' ||
    type === 'image' ||  // From upload form dropdown
    type.includes('pdf') ||
    type.includes('jpeg') ||
    type.includes('jpg') ||
    type.includes('png')
  );
};

/**
 * Check if document type is a PDF
 */
const isPdfType = (docType) => {
  if (!docType) return false;
  const type = docType.toLowerCase();
  return type === 'application/pdf' || type === 'pdf' || type.includes('pdf');
};

/**
 * Check if document type is an image
 * Note: The upload form stores 'image' for all image types
 */
const isImageType = (docType) => {
  if (!docType) return false;
  const type = docType.toLowerCase();
  return (
    type === 'image/jpeg' ||
    type === 'image/jpg' ||
    type === 'jpeg' ||
    type === 'jpg' ||
    type === 'image/png' ||
    type === 'png' ||
    type === 'image' ||  // From upload form dropdown - generic image type
    type.includes('jpeg') ||
    type.includes('jpg') ||
    type.includes('png')
  );
};

/**
 * Get normalized MIME type for embedding
 */
const getNormalizedMimeType = (docType) => {
  if (!docType) return null;
  const type = docType.toLowerCase();
  if (type.includes('pdf')) return 'application/pdf';
  if (type.includes('png')) return 'image/png';
  if (type.includes('jpg') || type.includes('jpeg')) return 'image/jpeg';
  return null;
};

/**
 * Fetch document from Supabase Storage
 * @param {string} filePath - Storage path
 * @returns {Promise<{data: ArrayBuffer, type: string}>}
 */
const fetchDocument = async (filePath) => {
  // Get signed URL
  const { data: signedUrlData, error: urlError } = await supabase.storage
    .from('case-documents')
    .createSignedUrl(filePath, 300); // 5 min expiry

  if (urlError) throw new Error(`Failed to get document URL: ${urlError.message}`);

  // Fetch the file
  const response = await fetch(signedUrlData.signedUrl);
  if (!response.ok) throw new Error(`Failed to fetch document: ${response.statusText}`);

  const arrayBuffer = await response.arrayBuffer();
  const contentType = response.headers.get('content-type') || 'application/octet-stream';

  return { data: arrayBuffer, type: contentType };
};

/**
 * Create a cover page for the document appendix
 * @param {PDFDocument} pdfDoc - PDF document to add page to
 * @param {Array} documents - List of documents being attached
 * @returns {Promise<void>}
 */
const addAppendixCoverPage = async (pdfDoc, documents) => {
  const page = pdfDoc.addPage();
  const { width, height } = page.getSize();
  const font = await pdfDoc.embedFont('Helvetica-Bold');
  const regularFont = await pdfDoc.embedFont('Helvetica');

  // Title
  page.drawText('DOCUMENT APPENDIX', {
    x: CONFIG.PAGE_MARGIN,
    y: height - 80,
    size: 24,
    font,
    color: rgb(0.15, 0.39, 0.92), // Blue
  });

  // Subtitle
  page.drawText(`${documents.length} attached document${documents.length !== 1 ? 's' : ''}`, {
    x: CONFIG.PAGE_MARGIN,
    y: height - 110,
    size: 12,
    font: regularFont,
    color: rgb(0.4, 0.4, 0.4),
  });

  // Divider line
  page.drawLine({
    start: { x: CONFIG.PAGE_MARGIN, y: height - 130 },
    end: { x: width - CONFIG.PAGE_MARGIN, y: height - 130 },
    thickness: 1,
    color: rgb(0.8, 0.8, 0.8),
  });

  // Document list
  let yPosition = height - 160;
  documents.forEach((doc, index) => {
    const docName = doc.document_name || `Document ${index + 1}`;
    const docType = doc.document_type || 'Unknown';
    const docSize = doc.file_size ? formatBytes(doc.file_size) : 'Unknown size';

    // Document number
    page.drawText(`${index + 1}.`, {
      x: CONFIG.PAGE_MARGIN,
      y: yPosition,
      size: 11,
      font,
      color: rgb(0.15, 0.39, 0.92),
    });

    // Document name
    page.drawText(docName, {
      x: CONFIG.PAGE_MARGIN + 25,
      y: yPosition,
      size: 11,
      font,
      color: rgb(0.1, 0.1, 0.1),
    });

    // Document info
    page.drawText(`${docType} • ${docSize}`, {
      x: CONFIG.PAGE_MARGIN + 25,
      y: yPosition - 14,
      size: 9,
      font: regularFont,
      color: rgb(0.5, 0.5, 0.5),
    });

    yPosition -= 40;

    // Add page break if needed
    if (yPosition < 100 && index < documents.length - 1) {
      // Would need a new page, but for simplicity we'll just stop listing
      page.drawText('...and more', {
        x: CONFIG.PAGE_MARGIN,
        y: yPosition,
        size: 10,
        font: regularFont,
        color: rgb(0.5, 0.5, 0.5),
      });
    }
  });

  // Footer note
  page.drawText('The following pages contain the attached documents.', {
    x: CONFIG.PAGE_MARGIN,
    y: 60,
    size: 10,
    font: regularFont,
    color: rgb(0.5, 0.5, 0.5),
  });
};

/**
 * Add a document separator page
 * @param {PDFDocument} pdfDoc - PDF document
 * @param {Object} docInfo - Document information
 * @param {number} index - Document index
 * @returns {Promise<void>}
 */
const addDocumentSeparator = async (pdfDoc, docInfo, index) => {
  const page = pdfDoc.addPage();
  const { width, height } = page.getSize();
  const font = await pdfDoc.embedFont('Helvetica-Bold');
  const regularFont = await pdfDoc.embedFont('Helvetica');

  // Background stripe
  page.drawRectangle({
    x: 0,
    y: height - 120,
    width: width,
    height: 80,
    color: rgb(0.95, 0.97, 1),
  });

  // Document number badge
  page.drawRectangle({
    x: CONFIG.PAGE_MARGIN,
    y: height - 100,
    width: 40,
    height: 40,
    color: rgb(0.15, 0.39, 0.92),
  });

  page.drawText(`${index + 1}`, {
    x: CONFIG.PAGE_MARGIN + (index < 10 ? 15 : 10),
    y: height - 85,
    size: 18,
    font,
    color: rgb(1, 1, 1),
  });

  // Document name
  const docName = docInfo.document_name || `Document ${index + 1}`;
  page.drawText(docName, {
    x: CONFIG.PAGE_MARGIN + 55,
    y: height - 75,
    size: 16,
    font,
    color: rgb(0.1, 0.1, 0.1),
  });

  // Document type and size
  const docType = docInfo.document_type || 'Unknown type';
  const docSize = docInfo.file_size ? formatBytes(docInfo.file_size) : '';
  page.drawText(`${docType}${docSize ? ` • ${docSize}` : ''}`, {
    x: CONFIG.PAGE_MARGIN + 55,
    y: height - 95,
    size: 10,
    font: regularFont,
    color: rgb(0.5, 0.5, 0.5),
  });

  // Description if available
  if (docInfo.description) {
    page.drawText(docInfo.description, {
      x: CONFIG.PAGE_MARGIN,
      y: height - 150,
      size: 11,
      font: regularFont,
      color: rgb(0.3, 0.3, 0.3),
      maxWidth: width - CONFIG.PAGE_MARGIN * 2,
    });
  }

  // Upload date
  const uploadDate = docInfo.uploaded_at 
    ? new Date(docInfo.uploaded_at).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : 'Unknown date';
  
  page.drawText(`Uploaded: ${uploadDate}`, {
    x: CONFIG.PAGE_MARGIN,
    y: 60,
    size: 9,
    font: regularFont,
    color: rgb(0.5, 0.5, 0.5),
  });
};

/**
 * Add a compact header bar to an existing PDF page
 * @param {PDFDocument} pdfDoc - PDF document (for embedding fonts)
 * @param {PDFPage} page - The page to add header to
 * @param {Object} docInfo - Document information
 * @param {number} index - Document index
 * @param {number} totalPages - Total pages in this document
 */
const addHeaderToPage = async (pdfDoc, page, docInfo, index, totalPages) => {
  const { width, height } = page.getSize();
  const font = await pdfDoc.embedFont('Helvetica-Bold');
  const regularFont = await pdfDoc.embedFont('Helvetica');
  const headerHeight = 25;

  // Semi-transparent header background at top
  page.drawRectangle({
    x: 0,
    y: height - headerHeight,
    width: width,
    height: headerHeight,
    color: rgb(0.15, 0.39, 0.92),
    opacity: 0.9,
  });

  // Document number
  page.drawText(`${index + 1}`, {
    x: 10,
    y: height - 17,
    size: 12,
    font,
    color: rgb(1, 1, 1),
  });

  // Document name
  const docName = docInfo.document_name || `Document ${index + 1}`;
  const displayName = docName.length > 60 ? docName.substring(0, 57) + '...' : docName;
  page.drawText(displayName, {
    x: 30,
    y: height - 17,
    size: 10,
    font,
    color: rgb(1, 1, 1),
  });

  // Page count on right side
  const pageInfo = totalPages > 1 ? `${totalPages} pages` : '1 page';
  const docSize = docInfo.file_size ? ` • ${formatBytes(docInfo.file_size)}` : '';
  page.drawText(`${pageInfo}${docSize}`, {
    x: width - 100,
    y: height - 17,
    size: 8,
    font: regularFont,
    color: rgb(0.9, 0.9, 0.9),
  });
};

/**
 * Merge documents into the main PDF
 * @param {Uint8Array} mainPdfBytes - Main PDF as bytes
 * @param {Array} documents - Array of document records from database
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Uint8Array>} - Merged PDF bytes
 */
export const mergeDocumentsIntoPDF = async (mainPdfBytes, documents, onProgress) => {
  // Validate inputs
  if (!documents || documents.length === 0) {
    return mainPdfBytes; // Nothing to merge
  }

  // Filter to supported types only (using flexible type matching)
  const supportedDocs = documents.filter(doc => isSupportedType(doc.document_type));

  if (supportedDocs.length === 0) {
    console.warn('No supported documents to attach');
    return mainPdfBytes;
  }

  // Limit number of documents
  const docsToMerge = supportedDocs.slice(0, CONFIG.MAX_DOCUMENTS);
  if (supportedDocs.length > CONFIG.MAX_DOCUMENTS) {
    console.warn(`Limiting to ${CONFIG.MAX_DOCUMENTS} documents (${supportedDocs.length} requested)`);
  }

  onProgress?.({ stage: 'preparing', percent: 5, message: 'Preparing document merge...' });

  // Load main PDF
  const mergedPdf = await PDFDocument.load(mainPdfBytes);

  // Add appendix cover page
  await addAppendixCoverPage(mergedPdf, docsToMerge);

  // Process each document
  let totalSize = 0;
  for (let i = 0; i < docsToMerge.length; i++) {
    const doc = docsToMerge[i];
    const progressPercent = 10 + Math.round((i / docsToMerge.length) * 80);
    
    onProgress?.({ 
      stage: 'merging', 
      percent: progressPercent, 
      message: `Attaching document ${i + 1} of ${docsToMerge.length}...`,
      currentDoc: doc.document_name,
    });

    try {
      // Fetch document
      const { data: docBytes, type: docType } = await fetchDocument(doc.file_path);
      totalSize += docBytes.byteLength;

      // Check total size limit
      if (totalSize > CONFIG.MAX_TOTAL_SIZE) {
        console.warn(`Total size limit reached at document ${i + 1}`);
        break;
      }

      // Merge based on type (using flexible type matching)
      const normalizedType = getNormalizedMimeType(doc.document_type) || docType;
      
      if (isPdfType(doc.document_type) || isPdfType(docType)) {
        // Merge PDF pages with header on first page
        const docPdf = await PDFDocument.load(docBytes);
        const pageIndices = docPdf.getPageIndices();
        const copiedPages = await mergedPdf.copyPages(docPdf, pageIndices);
        
        // Add header to first page only
        if (copiedPages.length > 0) {
          const firstPage = copiedPages[0];
          await addHeaderToPage(mergedPdf, firstPage, doc, i, copiedPages.length);
        }
        
        copiedPages.forEach(page => mergedPdf.addPage(page));
      } else if (isImageType(doc.document_type) || isImageType(docType)) {
        // Embed image with header on same page (no separate separator)
        console.log(`Embedding image: ${doc.document_name}, type: ${doc.document_type}, normalized: ${normalizedType}`);
        await embedImageWithHeader(mergedPdf, new Uint8Array(docBytes), normalizedType || doc.document_type || docType, doc, i);
      } else {
        console.warn(`Skipping unsupported document type: ${doc.document_type}`);
      }
    } catch (error) {
      console.error(`Error processing document ${doc.document_name}:`, error.message);
      // Continue with other documents
    }
  }

  onProgress?.({ stage: 'finalizing', percent: 95, message: 'Finalizing PDF...' });

  // Save merged PDF
  const mergedBytes = await mergedPdf.save();

  onProgress?.({ stage: 'complete', percent: 100, message: 'Export complete!' });

  return mergedBytes;
};

/**
 * Embed an image with a header on the same page
 * @param {PDFDocument} pdfDoc - PDF document
 * @param {Uint8Array} imageBytes - Image data
 * @param {string} mimeType - Image MIME type
 * @param {Object} docInfo - Document metadata
 * @param {number} index - Document index
 */
const embedImageWithHeader = async (pdfDoc, imageBytes, mimeType, docInfo, index) => {
  let image;
  const type = (mimeType || '').toLowerCase();
  
  // Check for JPEG (various formats)
  if (type === 'image/jpeg' || type === 'image/jpg' || type === 'jpeg' || type === 'jpg' || type.includes('jpeg') || type.includes('jpg')) {
    image = await pdfDoc.embedJpg(imageBytes);
  } 
  // Check for PNG (various formats)
  else if (type === 'image/png' || type === 'png' || type.includes('png')) {
    image = await pdfDoc.embedPng(imageBytes);
  } 
  // Try to detect from magic bytes if type is unclear
  else {
    // Check magic bytes for JPEG (FFD8FF) or PNG (89504E47)
    if (imageBytes[0] === 0xFF && imageBytes[1] === 0xD8 && imageBytes[2] === 0xFF) {
      console.log('Detected JPEG from magic bytes');
      image = await pdfDoc.embedJpg(imageBytes);
    } else if (imageBytes[0] === 0x89 && imageBytes[1] === 0x50 && imageBytes[2] === 0x4E && imageBytes[3] === 0x47) {
      console.log('Detected PNG from magic bytes');
      image = await pdfDoc.embedPng(imageBytes);
    } else {
      throw new Error(`Unsupported image type: ${mimeType}`);
    }
  }

  // A4 page size in points
  const pageWidth = 595;
  const pageHeight = 842;
  const headerHeight = 70; // Space for header
  const maxWidth = pageWidth - CONFIG.PAGE_MARGIN * 2;
  const maxHeight = pageHeight - CONFIG.PAGE_MARGIN * 2 - headerHeight;

  // Calculate dimensions to fit below header
  const imgWidth = image.width;
  const imgHeight = image.height;
  
  // Scale to fit
  let scale = 1;
  if (imgWidth > maxWidth || imgHeight > maxHeight) {
    scale = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
  }

  const scaledWidth = imgWidth * scale;
  const scaledHeight = imgHeight * scale;

  // Add page
  const page = pdfDoc.addPage([pageWidth, pageHeight]);
  const font = await pdfDoc.embedFont('Helvetica-Bold');
  const regularFont = await pdfDoc.embedFont('Helvetica');

  // Draw header background
  page.drawRectangle({
    x: 0,
    y: pageHeight - headerHeight,
    width: pageWidth,
    height: headerHeight,
    color: rgb(0.95, 0.97, 1),
  });

  // Document number badge
  page.drawRectangle({
    x: CONFIG.PAGE_MARGIN,
    y: pageHeight - 50,
    width: 30,
    height: 30,
    color: rgb(0.15, 0.39, 0.92),
  });

  page.drawText(`${index + 1}`, {
    x: CONFIG.PAGE_MARGIN + (index < 10 ? 10 : 6),
    y: pageHeight - 40,
    size: 14,
    font,
    color: rgb(1, 1, 1),
  });

  // Document name
  const docName = docInfo.document_name || `Document ${index + 1}`;
  page.drawText(docName.length > 50 ? docName.substring(0, 47) + '...' : docName, {
    x: CONFIG.PAGE_MARGIN + 40,
    y: pageHeight - 35,
    size: 12,
    font,
    color: rgb(0.1, 0.1, 0.1),
  });

  // Document type and size
  const docType = docInfo.document_type || 'Image';
  const docSize = docInfo.file_size ? formatBytes(docInfo.file_size) : '';
  page.drawText(`${docType}${docSize ? ` • ${docSize}` : ''}`, {
    x: CONFIG.PAGE_MARGIN + 40,
    y: pageHeight - 50,
    size: 9,
    font: regularFont,
    color: rgb(0.5, 0.5, 0.5),
  });

  // Center image below header
  const x = (pageWidth - scaledWidth) / 2;
  const y = (pageHeight - headerHeight - scaledHeight) / 2;

  // Draw image
  page.drawImage(image, {
    x,
    y: Math.max(CONFIG.PAGE_MARGIN, y), // Ensure it doesn't go below margin
    width: scaledWidth,
    height: scaledHeight,
  });
};

/**
 * Format bytes to human readable string
 */
const formatBytes = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

/**
 * Check if documents can be merged (pre-validation)
 * @param {Array} documents - Document records
 * @returns {Object} - { canMerge, warnings, supportedCount }
 */
export const validateDocumentsForMerge = (documents) => {
  const warnings = [];
  
  if (!documents || documents.length === 0) {
    return { canMerge: false, warnings: ['No documents to attach'], supportedCount: 0 };
  }

  // Use flexible type matching
  const supported = documents.filter(doc => isSupportedType(doc.document_type));

  const unsupported = documents.length - supported.length;
  if (unsupported > 0) {
    warnings.push(`${unsupported} document(s) skipped (unsupported format)`);
  }

  if (supported.length > CONFIG.MAX_DOCUMENTS) {
    warnings.push(`Only first ${CONFIG.MAX_DOCUMENTS} documents will be included`);
  }

  const totalSize = supported.reduce((sum, doc) => sum + (doc.file_size || 0), 0);
  if (totalSize > CONFIG.MAX_TOTAL_SIZE) {
    warnings.push(`Total size exceeds ${CONFIG.MAX_TOTAL_SIZE / 1024 / 1024}MB limit - some documents may be skipped`);
  }

  return {
    canMerge: supported.length > 0,
    warnings,
    supportedCount: Math.min(supported.length, CONFIG.MAX_DOCUMENTS),
    totalSize,
  };
};

export default {
  mergeDocumentsIntoPDF,
  validateDocumentsForMerge,
};
